/**
 * @file PlanItem.tsx
 * @description Component for displaying a single editorial plan item.
 */

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";

import { PlanDetails } from "../../types";
import { DownloadFormat } from "../../types/download";

import {
  downloadPlanContents,
  updateContentDownloadedStatus,
} from "../../services/contentService";
import { publishPlanContents } from "../../services/wordpressService";
import { updatePlanName } from "../../services/planService";
import log from "../../config/logging";

import DownloadButton from "./DownloadButton";
import WordPressPublishButton from "./WordPressPublishButton";

interface PlanItemProps {
  plan: PlanDetails;
  onPlanUpdated?: (updatedPlan: PlanDetails) => void;
}

const PlanItem: React.FC<PlanItemProps> = ({ plan, onPlanUpdated }) => {
  const navigate = useNavigate();
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPublishingToWordPress, setIsPublishingToWordPress] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingTitle, setEditingTitle] = useState("");
  const [isSavingTitle, setIsSavingTitle] = useState(false);
  const [isHoveringTitle, setIsHoveringTitle] = useState(false);

  const handleOpen = () => {
    navigate(`/piano/${plan.id}`);
  };

  const handleDownload = async (format: DownloadFormat) => {
    try {
      setIsDownloading(true);
      await downloadPlanContents(plan.id, plan.name, format);

      // Update downloaded status for all contents in the plan
      if (plan.contents && plan.contents.length > 0) {
        await Promise.all(
          plan.contents.map((content) =>
            updateContentDownloadedStatus(content.id)
          )
        );
      }

      toast.success(
        `Piano editoriale scaricato in formato ${
          format === "wxr" ? "WordPress XML" : "Word"
        }`
      );

      log.info(`Plan downloaded successfully as ${format}`, {
        planId: plan.id,
        format,
      });
    } catch (error) {
      log.error(`Download failed for format: ${format}`, {
        error,
        planId: plan.id,
      });

      toast.error(
        `Errore durante il download in formato ${
          format === "wxr" ? "WordPress XML" : "Word"
        }`
      );
    } finally {
      setIsDownloading(false);
    }
  };

  const handlePublishToWordPress = async () => {
    try {
      setIsPublishingToWordPress(true);
      const result = await publishPlanContents(plan.id);
      
      const successCount = result.summary.success;
      const failureCount = result.summary.failed;
      
      if (successCount > 0) {
        toast.success(
          `Pubblicati ${successCount} contenuto/i su WordPress${failureCount > 0 ? `, ${failureCount} fallito/i` : ""}`
        );
      } else {
        toast.error("Nessun contenuto pubblicato con successo");
      }
      
      log.info("Plan contents published to WordPress", {
        planId: plan.id,
        summary: result.summary,
      });
    } catch (error) {
      log.error("Error publishing plan contents to WordPress", { error });
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Errore durante la pubblicazione su WordPress";
      toast.error(errorMessage);
    } finally {
      setIsPublishingToWordPress(false);
    }
  };

  const handleOpenEditModal = () => {
    setEditingTitle(plan.name);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingTitle("");
  };

  const handleSaveTitle = async () => {
    const trimmedTitle = editingTitle.trim();
    if (!trimmedTitle) {
      toast.error("Il titolo non può essere vuoto");
      return;
    }

    if (trimmedTitle === plan.name) {
      handleCloseEditModal();
      return;
    }

    try {
      setIsSavingTitle(true);
      await updatePlanName(plan.id, trimmedTitle);
      const updatedPlan = { ...plan, name: trimmedTitle };
      if (onPlanUpdated) {
        onPlanUpdated(updatedPlan);
      }
      handleCloseEditModal();
      toast.success("Titolo aggiornato con successo");
      log.info("Plan title updated successfully", { planId: plan.id, newTitle: trimmedTitle });
    } catch (error) {
      log.error("Error updating plan title", { error, planId: plan.id });
      toast.error("Errore durante l'aggiornamento del titolo");
    } finally {
      setIsSavingTitle(false);
    }
  };

  return (
    <>
      <li className="flex flex-col items-start justify-between gap-8 p-8 mb-4 xl:gap-12 rounded-3xl bg-primary text-primary-content">
        <div className="flex flex-col items-start gap-2 w-full">
          <ArticleOutlinedIcon className="w-10 h-10" />
          <div 
            className="flex items-center gap-2 flex-wrap"
            onMouseEnter={() => setIsHoveringTitle(true)}
            onMouseLeave={() => setIsHoveringTitle(false)}
          >
            <h3 className="!mb-0 !text-2xl !font-light">{plan.name}</h3>
            <button
              onClick={handleOpenEditModal}
              className={`p-1.5 rounded-full btn btn-ghost btn-sm transition-opacity flex-shrink-0 ${
                isHoveringTitle ? "opacity-100" : "opacity-0"
              }`}
              title="Modifica titolo"
            >
              <EditOutlinedIcon className="w-5 h-5 text-white" />
            </button>
          </div>
          <p className="text-base opacity-50">
            {new Date(plan.createdAt).toLocaleDateString()}
          </p>
        </div>
      <div className="flex gap-3">
        <button
          onClick={handleOpen}
          className="text-lg text-white border-white rounded-full btn btn-outline hover:text-primary hover:bg-white hover:border-white"
        >
          Apri
        </button>
        <DownloadButton
          onDownload={handleDownload}
          isLoading={isDownloading}
          variant="outline-white"
        />
        <WordPressPublishButton
          onPublish={handlePublishToWordPress}
          isLoading={isPublishingToWordPress}
          disabled={isDownloading}
          variant="outline-white"
        />
      </div>
    </li>

    {/* Edit Title Modal */}
    {isEditModalOpen && (
      <dialog className="modal modal-open">
        <div className="modal-box">
          <h3 className="font-bold text-lg mb-4">Modifica titolo piano editoriale</h3>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <label htmlFor="edit-plan-title" className="text-sm font-medium">
                Titolo del piano editoriale
              </label>
              <input
                id="edit-plan-title"
                type="text"
                value={editingTitle}
                onChange={(e) => setEditingTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleSaveTitle();
                  } else if (e.key === "Escape") {
                    e.preventDefault();
                    handleCloseEditModal();
                  }
                }}
                disabled={isSavingTitle}
                className="w-full rounded-2xl border border-gray-300 input"
                placeholder="Inserisci il titolo del piano editoriale"
                autoFocus
              />
            </div>
            <div className="modal-action">
              <button
                onClick={handleCloseEditModal}
                disabled={isSavingTitle}
                className="px-6 text-base rounded-full btn btn-outline btn-primary"
              >
                Annulla
              </button>
              <button
                onClick={handleSaveTitle}
                disabled={isSavingTitle}
                className="px-6 text-base rounded-full btn btn-primary"
              >
                {isSavingTitle ? "Salvataggio..." : "Salva"}
              </button>
            </div>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={handleCloseEditModal}>close</button>
        </form>
      </dialog>
    )}
    </>
  );
};

export default PlanItem;
